import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Product {
  id?: string;
  name: string;
  price: number;
  original_price?: number;
  description: string;
  category: 'carteras' | 'accesorios';
  stock: number;
  image_url: string;
  is_featured: boolean;
  is_new: boolean;
  is_on_sale: boolean;
}

interface ProductManagerProps {
  onProductAdded: () => void;
}

export default function ProductManager({ onProductAdded }: ProductManagerProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  
  const [formData, setFormData] = useState<Product>({
    name: '',
    price: 0,
    original_price: 0,
    description: '',
    category: 'carteras',
    stock: 0,
    image_url: '',
    is_featured: false,
    is_new: false,
    is_on_sale: false,
  });

  useEffect(() => {
    fetchProducts();
    initializeStorage();
  }, []);

  const initializeStorage = async () => {
    try {
      // Verificar si el bucket existe, si no, crearlo
      const { data: buckets } = await supabase.storage.listBuckets();
      const bucketExists = buckets?.some(bucket => bucket.name === 'product-images');
      
      if (!bucketExists) {
        await supabase.storage.createBucket('product-images', {
          public: true,
          allowedMimeTypes: ['image/png', 'image/jpeg', 'image/jpg'],
          fileSizeLimit: 10485760 // 10MB
        });
      }
    } catch (error) {
      console.log('Storage initialization:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de archivo
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setUploadError('Solo se permiten archivos PNG, JPG y JPEG');
        return;
      }

      // Validar tamaño (10MB máximo)
      if (file.size > 10485760) {
        setUploadError('El archivo no puede ser mayor a 10MB');
        return;
      }

      setUploadError('');
      setImageFile(file);
      
      // Crear preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    setUploadingImage(true);
    setUploadError('');
    
    try {
      // Crear un nombre único para el archivo
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const fileName = `product-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      // Subir archivo a Supabase Storage
      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        });

      if (error) {
        console.error('Upload error:', error);
        throw new Error(`Error al subir imagen: ${error.message}`);
      }

      // Obtener URL pública
      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error: any) {
      console.error('Error uploading image:', error);
      setUploadError(error.message || 'Error al subir la imagen');
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setUploadError('');

    try {
      let imageUrl = formData.image_url;

      // Si hay una nueva imagen, subirla
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      // Validar que tengamos una imagen
      if (!imageUrl) {
        throw new Error('Se requiere una imagen para el producto');
      }

      const productData = {
        ...formData,
        image_url: imageUrl,
        original_price: formData.is_on_sale && formData.original_price ? formData.original_price : null,
      };

      if (editingProduct) {
        // Actualizar producto existente
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingProduct.id);

        if (error) throw error;
      } else {
        // Crear nuevo producto
        const { error } = await supabase
          .from('products')
          .insert([productData]);

        if (error) throw error;
      }

      // Resetear formulario
      setFormData({
        name: '',
        price: 0,
        original_price: 0,
        description: '',
        category: 'carteras',
        stock: 0,
        image_url: '',
        is_featured: false,
        is_new: false,
        is_on_sale: false,
      });
      setImageFile(null);
      setImagePreview('');
      setShowForm(false);
      setEditingProduct(null);
      setUploadError('');
      
      await fetchProducts();
      onProductAdded();
      
      alert('Producto guardado exitosamente');
    } catch (error: any) {
      console.error('Error saving product:', error);
      setUploadError(error.message || 'Error al guardar el producto');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData(product);
    setImagePreview(product.image_url);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este producto?')) return;

    try {
      // Primero obtener la información del producto para eliminar la imagen
      const { data: product } = await supabase
        .from('products')
        .select('image_url')
        .eq('id', id)
        .single();

      // Eliminar el producto de la base de datos
      const { error: deleteError } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // Si el producto tenía una imagen en nuestro storage, intentar eliminarla
      if (product?.image_url && product.image_url.includes('supabase')) {
        try {
          const fileName = product.image_url.split('/').pop();
          if (fileName) {
            await supabase.storage
              .from('product-images')
              .remove([fileName]);
          }
        } catch (storageError) {
          console.log('No se pudo eliminar la imagen del storage:', storageError);
          // No fallar si no se puede eliminar la imagen
        }
      }
      
      await fetchProducts();
      onProductAdded();
      alert('Producto eliminado exitosamente');
    } catch (error: any) {
      console.error('Error deleting product:', error);
      alert(`Error al eliminar el producto: ${error.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: 0,
      original_price: 0,
      description: '',
      category: 'carteras',
      stock: 0,
      image_url: '',
      is_featured: false,
      is_new: false,
      is_on_sale: false,
    });
    setImageFile(null);
    setImagePreview('');
    setShowForm(false);
    setEditingProduct(null);
    setUploadError('');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Gestión de Productos</h2>
        <button
          onClick={() => setShowForm(true)}
          className="bg-[#C6A45C] text-black px-6 py-2 rounded-lg hover:bg-[#B8965A] transition-colors font-medium"
        >
          Agregar Producto
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">
                {editingProduct ? 'Editar Producto' : 'Agregar Nuevo Producto'}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Subida de Imagen */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Imagen del Producto *
                </label>
                
                {/* Error de subida */}
                {uploadError && (
                  <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg text-red-300 text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    {uploadError}
                  </div>
                )}
                
                {/* Preview de imagen */}
                {imagePreview && (
                  <div className="mb-4">
                    <div className="w-32 h-32 mx-auto bg-gray-800 rounded-lg overflow-hidden border-2 border-[#C6A45C]">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-center text-green-400 text-sm mt-2">
                      <i className="ri-check-line mr-1"></i>
                      Imagen seleccionada correctamente
                    </p>
                  </div>
                )}

                {/* Input de archivo */}
                <div className="relative">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleImageChange}
                    className="hidden"
                    id="image-upload"
                    required={!editingProduct && !imagePreview}
                  />
                  <label
                    htmlFor="image-upload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-[#C6A45C] transition-colors"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
                      <p className="text-sm text-gray-400">
                        <span className="font-semibold">Haz clic para subir</span> o arrastra una imagen
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG, JPEG (MAX. 10MB)</p>
                    </div>
                  </label>
                </div>

                {uploadingImage && (
                  <div className="text-center text-[#C6A45C] mt-2">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Subiendo imagen...
                  </div>
                )}
              </div>

              {/* Resto del formulario */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Nombre *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full p-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-[#C6A45C] focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Categoría *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as 'carteras' | 'accesorios' })}
                    className="w-full p-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-[#C6A45C] focus:outline-none pr-8"
                    required
                  >
                    <option value="carteras">Carteras</option>
                    <option value="accesorios">Accesorios</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Precio Actual *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                    className="w-full p-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-[#C6A45C] focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Precio Original 
                    <span className="text-gray-400 text-sm ml-1">(se mostrará tachado si está en oferta)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.original_price || ''}
                    onChange={(e) => setFormData({ ...formData, original_price: parseFloat(e.target.value) || 0 })}
                    className="w-full p-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-[#C6A45C] focus:outline-none"
                    placeholder="Ej: 150.00"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Stock *</label>
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) || 0 })}
                    className="w-full p-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-[#C6A45C] focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full p-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-[#C6A45C] focus:outline-none resize-none"
                  placeholder="Describe las características del producto..."
                />
              </div>

              {/* Checkboxes */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="w-5 h-5 text-[#C6A45C] bg-gray-800 border-gray-600 rounded focus:ring-[#C6A45C] focus:ring-2"
                  />
                  <span className="text-white">Producto Destacado</span>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_new}
                    onChange={(e) => setFormData({ ...formData, is_new: e.target.checked })}
                    className="w-5 h-5 text-[#C6A45C] bg-gray-800 border-gray-600 rounded focus:ring-[#C6A45C] focus:ring-2"
                  />
                  <span className="text-white">Producto NUEVO</span>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_on_sale}
                    onChange={(e) => setFormData({ ...formData, is_on_sale: e.target.checked })}
                    className="w-5 h-5 text-[#C6A45C] bg-gray-800 border-gray-600 rounded focus:ring-[#C6A45C] focus:ring-2"
                  />
                  <span className="text-white">En OFERTA</span>
                </label>
              </div>

              {/* Información adicional sobre precios */}
              <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <h4 className="text-white font-medium mb-2 flex items-center">
                  <i className="ri-information-line text-[#C6A45C] mr-2"></i>
                  Información sobre precios
                </h4>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• <strong>Precio Actual:</strong> El precio de venta que verán los clientes</li>
                  <li>• <strong>Precio Original:</strong> Se mostrará tachado cuando el producto esté en oferta</li>
                  <li>• <strong>En OFERTA:</strong> Marca esta casilla para mostrar el precio original tachado</li>
                </ul>
              </div>

              {/* Botones */}
              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  disabled={isLoading || uploadingImage}
                  className="flex-1 bg-[#C6A45C] text-black py-3 rounded-lg hover:bg-[#B8965A] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Guardando...
                    </>
                  ) : (
                    editingProduct ? 'Actualizar Producto' : 'Crear Producto'
                  )}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista de productos */}
      <div className="bg-gray-900 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Productos Existentes</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) => (
            <div key={product.id} className="bg-gray-800 rounded-lg p-4">
              <div className="aspect-square bg-gray-700 rounded-lg mb-3 overflow-hidden">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="text-white font-medium mb-2">{product.name}</h4>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#C6A45C] font-bold">${product.price}</span>
                <span className="text-gray-400 text-sm">{product.category}</span>
              </div>
              <div className="flex space-x-2 mb-3">
                {product.is_featured && (
                  <span className="bg-[#C6A45C] text-black text-xs px-2 py-1 rounded">Destacado</span>
                )}
                {product.is_new && (
                  <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">NUEVO</span>
                )}
                {product.is_on_sale && (
                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">OFERTA</span>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(product)}
                  className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition-colors text-sm whitespace-nowrap"
                >
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(product.id!)}
                  className="flex-1 bg-red-600 text-white py-2 rounded hover:bg-red-700 transition-colors text-sm whitespace-nowrap"
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
